import React, { useEffect, useState, useCallback } from "react";
import { RefreshCw, Users, Calendar, TrendingUp, MessageCircle, Clock3, Lock } from "lucide-react";
import "./LeadsPage.css";

const API = process.env.REACT_APP_BACKEND_URL;
const SESSION_KEY = "bja_leads_key";

const timeAgo = (iso) => {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const mins = Math.max(0, Math.round((Date.now() - t) / 60000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

export default function LeadsPage() {
  const [key, setKey] = useState(() => sessionStorage.getItem(SESSION_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [channelFilter, setChannelFilter] = useState("all");

  const load = useCallback(async (k) => {
    setLoading(true);
    setError("");
    try {
      const r = await fetch(`${API}/api/landing/leads/overview?key=${encodeURIComponent(k)}`);
      if (r.status === 403) throw new Error("Wrong access key");
      if (!r.ok) throw new Error("Couldn't load leads");
      const d = await r.json();
      setData(d);
      setAuthed(true);
      sessionStorage.setItem(SESSION_KEY, k);
    } catch (e) {
      setError(e.message);
      setAuthed(false);
      sessionStorage.removeItem(SESSION_KEY);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (key) load(key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!authed) {
    return (
      <div className="leads-page" data-testid="leads-gate">
        <form
          className="leads-gate-card"
          onSubmit={(e) => { e.preventDefault(); load(key); }}
        >
          <Lock className="w-6 h-6" />
          <h1>Leads Dashboard</h1>
          <p>Enter the access key to view leads.</p>
          <input
            type="password"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            placeholder="Access key"
            data-testid="leads-key-input"
            autoFocus
          />
          {error && <div className="leads-error" data-testid="leads-gate-error">{error}</div>}
          <button type="submit" disabled={loading || !key.trim()} data-testid="leads-gate-submit">
            {loading ? "Checking…" : "Open dashboard"}
          </button>
        </form>
      </div>
    );
  }

  const rows = (data?.leads || []).filter((l) => channelFilter === "all" || l.channel === channelFilter);

  return (
    <div className="leads-page" data-testid="leads-dashboard">
      <header className="leads-head">
        <div>
          <h1>Leads</h1>
          <p className="leads-sub">Live from the landing page forms</p>
        </div>
        <button className="leads-refresh" onClick={() => load(key)} data-testid="leads-refresh">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </header>

      <div className="leads-stats" data-testid="leads-stats">
        <div className="stat-card">
          <Users className="w-4 h-4" />
          <span className="stat-num" data-testid="stat-total">{data?.total ?? 0}</span>
          <span className="stat-lbl">Total leads</span>
        </div>
        <div className="stat-card">
          <Calendar className="w-4 h-4" />
          <span className="stat-num" data-testid="stat-today">{data?.today ?? 0}</span>
          <span className="stat-lbl">Today</span>
        </div>
        <div className="stat-card">
          <TrendingUp className="w-4 h-4" />
          <span className="stat-num" data-testid="stat-week">{data?.week ?? 0}</span>
          <span className="stat-lbl">Last 7 days</span>
        </div>
        <div className="stat-card stat-meta">
          <span className="stat-num" data-testid="stat-meta">{data?.meta_total ?? 0}</span>
          <span className="stat-lbl">From Meta ads</span>
          <span className="stat-sub">{data?.meta_today ?? 0} today · {data?.meta_week ?? 0} this week</span>
        </div>
        <div className="stat-card stat-chatgpt">
          <span className="stat-num" data-testid="stat-chatgpt">{data?.chatgpt_total ?? 0}</span>
          <span className="stat-lbl">From ChatGPT ads</span>
          <span className="stat-sub">{data?.chatgpt_today ?? 0} today · {data?.chatgpt_week ?? 0} this week</span>
        </div>
        <div className="stat-card stat-other">
          <span className="stat-num" data-testid="stat-other">{data?.other_total ?? 0}</span>
          <span className="stat-lbl">Other / Direct</span>
        </div>
        <div className="stat-card stat-waiting">
          <Clock3 className="w-4 h-4" />
          <span className="stat-num" data-testid="stat-waiting">{data?.waiting ?? 0}</span>
          <span className="stat-lbl">Waiting for contact</span>
        </div>
      </div>

      <label className="leads-filter">
        Show:
        <select
          value={channelFilter}
          onChange={(e) => setChannelFilter(e.target.value)}
          data-testid="leads-channel-filter"
        >
          <option value="all">All leads</option>
          <option value="meta">Meta ads only</option>
          <option value="chatgpt">ChatGPT ads only</option>
          <option value="other">Other / Direct only</option>
        </select>
      </label>

      <div className="leads-list" data-testid="leads-list">
        {rows.length === 0 && <p className="leads-empty">No leads yet.</p>}
        {rows.map((l) => (
          <div className="lead-row" key={l.id} data-testid={`lead-row-${l.id}`}>
            <div className="lead-main">
              <div className="lead-title">
                <strong>{l.first_name}</strong>
                {l.channel === "meta" && <span className="badge badge-meta" data-testid={`lead-badge-meta-${l.id}`}>Meta</span>}
                {l.channel === "chatgpt" && <span className="badge badge-chatgpt" data-testid={`lead-badge-chatgpt-${l.id}`}>ChatGPT</span>}
                {l.contacted_at
                  ? <span className="badge badge-done">Contacted {timeAgo(l.contacted_at)}</span>
                  : <span className="badge badge-waiting">Waiting</span>}
              </div>
              <div className="lead-meta">
                {l.target_role} · {l.source}
                {l.utm_campaign ? ` · campaign: ${l.utm_campaign}` : ""}
              </div>
              <div className="lead-meta dim">{l.email} · {l.whatsapp_e164}</div>
            </div>
            <div className="lead-side">
              <span className="lead-time">{timeAgo(l.created_at)}</span>
              <a
                className="lead-wa"
                href={`${API}/api/landing/leads/${l.id}/contact`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`lead-contact-${l.id}`}
              >
                <MessageCircle className="w-4 h-4" /> Message
              </a>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
